import React, { useEffect, useState, useCallback, useRef } from "react";
import { Col, Row, Overlay, Popover } from "react-bootstrap";
import Admission from "./admission";
import Hamburger from "../../../layout/kebab";
import { toDateOrNull } from "../../../utils/object";

const makeClientId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `tmp_${Date.now()}_${Math.random().toString(16).slice(2)}`;

const normalizeAdmission = (item = {}) => ({
  ...(item || {}),
  _cid: item?._cid,
  uuid: item?.uuid ?? "",
  jurisdiction: item?.jurisdiction ?? "",
  courtName: item?.courtName ?? "",
  admissionType: item?.admissionType ?? "state_bar",
  barNumber: item?.barNumber ?? "",
  admissionDate: toDateOrNull(item?.admissionDate),
  status: item?.status ?? "active",
  isPrimary: Boolean(item?.isPrimary),
  meta:
    item?.meta && typeof item.meta === "object" && !Array.isArray(item.meta)
      ? item.meta
      : {},
});

function ensureIds(arr) {
  return (Array.isArray(arr) ? arr : []).map((item) => {
    const normalized = normalizeAdmission(item);
    if (normalized?.uuid || normalized?._cid) return normalized;
    return { ...normalized, _cid: makeClientId() };
  });
}

function Admissions(props) {
  const { value, onChange } = props;

  const [admissionsState, setAdmissionsState] = useState(() => ensureIds(value));
  const [menuState, setMenuState] = useState({
    show: false,
    target: null,
  });

  const syncingFromPropsRef = useRef(false);

  useEffect(() => {
    syncingFromPropsRef.current = true;
    setAdmissionsState(ensureIds(value));

    Promise.resolve().then(() => {
      syncingFromPropsRef.current = false;
    });
  }, [value]);

  useEffect(() => {
    if (!onChange) return;
    if (syncingFromPropsRef.current) return;
    onChange(admissionsState);
  }, [admissionsState, onChange]);

  const closeHamburger = useCallback(() => {
    setMenuState({
      show: false,
      target: null,
    });
  }, []);

  const openHamburger = useCallback((event) => {
    event.preventDefault();
    setMenuState({
      show: true,
      target: event.currentTarget,
    });
  }, []);

  const onAdmissionAdd = useCallback(
    (e) => {
      e?.preventDefault?.();

      setAdmissionsState((prev) => {
        const next = [...ensureIds(prev)];
        next.push(
          normalizeAdmission({
            _cid: makeClientId(),
            isPrimary: next.length === 0,
          })
        );
        return next;
      });

      closeHamburger();
    },
    [closeHamburger]
  );

  const onAdmissionChange = useCallback(({ index, values }) => {
    setAdmissionsState((prev) => {
      const next = [...ensureIds(prev)];
      const current = normalizeAdmission(next[index] || {});
      const incoming = normalizeAdmission(values || {});

      next[index] = {
        ...current,
        ...incoming,
        meta: incoming.meta ?? {},
      };

      if (next[index]?.isPrimary) {
        for (let i = 0; i < next.length; i += 1) {
          next[i] = {
            ...next[i],
            isPrimary: i === index,
          };
        }
      }

      return next;
    });
  }, []);

  const onAdmissionDelete = useCallback(({ index }) => {
    setAdmissionsState((prev) => {
      const next = [...ensureIds(prev)];
      const removed = next[index];
      next.splice(index, 1);

      if (removed?.isPrimary && next.length > 0) {
        next[0] = {
          ...next[0],
          isPrimary: true,
        };
      }

      return next;
    });
  }, []);

  return (
    <>
      <Col xs="12">
        <Row className="g-3">
          <Col xs="11">
            <h2 className="small-title">
              Admissions &nbsp;
            </h2>
          </Col>
          <Col xs="1">
            <Hamburger
              variant="link"
              vertical={false}
              onClick={(e) => openHamburger(e)}
            />
          </Col>
        </Row>

        {Array.isArray(admissionsState) &&
          admissionsState.map((item, index) => (
            <Admission
              key={item?.uuid || item?._cid || `admission.${index}`}
              {...item}
              index={index}
              backgroundColor={index % 2 === 0 ? "#e9f8fb" : "white"}
              onChange={onAdmissionChange}
              onDelete={onAdmissionDelete}
            />
          ))}
      </Col>

      <Overlay
        target={menuState.target}
        show={menuState.show}
        placement="bottom-end"
        rootClose
        onHide={closeHamburger}
      >
        {(overlayProps) => (
          <Popover {...overlayProps}>
            <Popover.Body>
              <Row className="mb-2">
                <Col className="d-flex flex-row mt-1">
                  <button
                    type="button"
                    className="btn btn-link p-0 me-2 btn-sm small text-start"
                    onClick={onAdmissionAdd}
                  >
                    New
                  </button>
                </Col>
              </Row>
            </Popover.Body>
          </Popover>
        )}
      </Overlay>
    </>
  );
}

Admissions.defaultProps = {
  value: [],
  onChange: null,
};

export default Admissions;